import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme, ThemeTransitionDuration } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Popover, { ContentBodyProps, PopoverProps } from "@insite/mobius/Popover";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import styled, { ThemeProps, withTheme } from "styled-components";


export interface QuickMenuPresentationProps {
    /** An object containing props to be passed down to trigger button component.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<QuickMenuProps>;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<QuickMenuProps>;
        menu?: StyledProp<QuickMenuProps>;
        menuItem?: StyledProp<QuickMenuProps>;
    };
    /** Props to be passed into the trigger Icon component.
     * @themable */
    iconProps?: IconPresentationProps;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** Theme transition length for appearance.
     * @themable */
    transitionDuration?: keyof ThemeTransitionDuration;
    /** override the default max height */
    maxHeight?: string;
    /** Boolean value indicating that there is a portal modal inside the menu, which affects popover click outside functionality */
    hasChildPortal?: boolean;
}

export type QuickMenuComponentProps = MobiusStyledComponentProps<
    "ul",
    Pick<PopoverProps, "isOpen" | "onClose" | "onOpen"> & {
        /** Where the menu origin is located in relation to the trigger button. */
        position?: "start" | "middle" | "end";
        /** Where the top or bottom external popover corner is located vertically in relation to the trigger. */
        yPosition?: "top" | "bottom";
        /**
         * Unique id to be passed into the popover element.
         * If not provided, a random id is assigned (an id is required for accessibility purposes).
         */
        uid?: string;
        /** Used for targeting with an automated test. */
        "data-test-selector"?: string;
    }
>;

export type QuickMenuProps = QuickMenuComponentProps & QuickMenuPresentationProps;

const QuickButton = styled(Button)`
    z-index: ${props => getProp("theme.zIndex.menu", 0)(props) - 1};
    padding: 0;
    margin-top: .5rem;
    width: 7.5rem;
    color: #002554;
    font-family: 'Roboto', sans-serif;
    font-size: .875rem;
    font-weight: bold;
    line-height: 2.3rem;
    text-align: center;
    background: #ffffff;
    border-color: #002554;
    outline: none !important;
    > .btn-txt {
        display: inline-block;
        width: 7.5rem;
        padding: 0 1.5625rem 0 .9375rem;
        height: 2.5rem;
        line-height: 2.3rem;
    }
    &:before {
        display: inline-block;
        content: "+";
        font-weight: 400;
        font-size: 1.25rem;
        line-height: 1;
        top: 1.125rem;
        right: .75rem;
        position: absolute;
    }
    @media screen and (max-width: 1080px) and (min-width: 1024px) {
        margin-left: .31250625rem;
    }
    &:hover {
        border-color: #2060ad;
        border-radius: .125rem;
        background-color: #2060ad;
        color: #ddecfe;
    }
    &:focus {
        border-radius: .125rem;
        background-color: #002554;
        outline-offset: 0;
        outline-width: 0;
        color: #ffffff;
        border-color: transparent;
        outline: 2px dashed #2060ad;
        border-radius: .125rem;
        line-height: 2.3rem;
        > .btn-txt {
            display: inline-block;
            width: 7.5rem;
            padding: 0 1.5625rem 0 .9375rem;
            height: 2.5rem;
            line-height: 2.3rem;
        }
    }
    &:active {
        border-color: #002554;
        border-radius: .125rem;
        background-color: #002554;
        line-height: 2.3rem;
        color: #ffffff;
        > .btn-txt {
            display: inline-block;
            height: 2.5rem;
            line-height: 2.3rem;
        }
    }
    &:focus-visible {
        border-color: transparent;
        outline: 2px dashed #2060ad;
        border-radius: .125rem;
    }
    [aria-expanded="true"]&:after {
        content: "";
        position: absolute;
        top: 2.625rem;
        left: 42%;
        width: 0;
        z-index: 9999;
        height: 0;
        border-bottom: solid 10px #fff;
        border-left: solid 10px transparent;
        border-right: solid 10px transparent;
    }
`;

const QuickMenuItem = styled.li`
  
    span,
    button,
    a {
        display: block;
        padding: none;
        ${injectCss}
    },
    top: 50px !important;
    height: auto;
    max-height: unset;
    overflow: hidden;
    padding: 1rem 1.5rem 1.5rem;
    left: unset !important;
    right: -8.438rem;
    > div > div > div > div {
        &:last-child {
            > div {
                    background-color: #F3F4F5;
                    margin: 0 -1.5rem -1.5rem -1.5rem;
                    padding: 1.25rem;
            }

        }
    }
` as any; // The returned type doesn't have the expected "css" property.

type Props = QuickMenuProps & ThemeProps<BaseTheme> & { isOpen?: boolean };
type State = {
    controlsId: string;
    deleteListItemsModalIsOpen: boolean;
};

/**
 * An icon button that triggers an overflow menu of `Clickable` children.
 */
class QuickMenu extends React.Component<Props, State> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<HTMLUListElement>();

    constructor(props: Props) {
        super(props);
        this.state = {
            controlsId: this.props.uid || uniqueId(),
            deleteListItemsModalIsOpen: false,
        };
    }

    closeMenuPopup = () => { 
        this.setState({ deleteListItemsModalIsOpen: true });

        if (typeof document !== "undefined") {
            const quickBuy = document.querySelector('button[data-test-selector="popoverOverflowTrigger"]')as HTMLElement;
            if (quickBuy.classList.contains('modal-open')) {
                quickBuy.classList.remove('modal-open');
            }
        }
    };

    render() {
        const {
            children,
            isOpen,
            onClose,
            onOpen,
            position,
            transitionDuration,
            yPosition,
            mergeCss,
            maxHeight,
            hasChildPortal,
            ...otherProps
        } = this.props;
        const { controlsId } = this.state;
        const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "menu" });
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.overflowMenu?.defaultProps?.mergeCss;
        // const iconProps = spreadProps("iconProps" as any);
        const menuItems: JSX.Element[] = React.Children.map(children, (menuChild, index) => {
            const newProps: { onClick?: (event: Event) => void } = {};
            if (typeof (menuChild as React.ReactElement)?.props?.onClick === "function") {
                newProps.onClick = event => {
                    (menuChild as React.ReactElement)?.props?.onClick(event);
                    this.popover.current && (this.popover.current! as any).closePopover();
                };
            }
            if (!menuChild) {
                return <></>;
            }
            return (
                // eslint-disable-next-line react/no-array-index-key
                <QuickMenuItem key={index} css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}>
                    {React.cloneElement(menuChild as React.ReactElement, newProps)}
                </QuickMenuItem>
            );
        });

        const popoverBodyProps: ContentBodyProps = {
            uid: controlsId,
            _width: 400,
            _height: "auto",
            ...omitSingle(otherProps, "cssOverrides"),
        };
        const AddBodyClass = () => {
            if (typeof document !== "undefined") {
                const quickBuy = document.querySelector('button[data-test-selector="popoverOverflowTrigger"]')as HTMLElement;
                quickBuy?.classList.add('modal-open');
            }
        };
        const popoverTrigger = (
            <QuickButton
                {...spreadProps("buttonProps" as any)}
                type="button"
                data-test-selector={otherProps["data-test-selector"] ?? "popoverOverflowTrigger"}
                className="quick-buy"
            >
                <Typography as="span" className="btn-txt" onClick={AddBodyClass}>{translate("Quick Buy")}</Typography>
                <VisuallyHidden>{otherProps.theme.translate("Menu")}</VisuallyHidden>
            </QuickButton>
        );
        return (
            <>
                <Popover
                    ref={this.popover}
                    contentBodyProps={popoverBodyProps}
                    wrapperProps={{
                        css: applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss),
                        _width: "7.5rem",
                        _height: "2.5rem",
                    }}
                    popoverTrigger={popoverTrigger}
                    onClose={onClose}
                    onOpen={onOpen}
                    zIndexKey="menu"
                    isOpen={isOpen}
                    insideRefs={[this.element]}
                    transitionDuration={transitionDuration}
                    xPosition={position}
                    yPosition={yPosition}
                    _height="auto"
                    hasChildPortal={hasChildPortal}
                >
                    {menuItems}
                </Popover>
                {this.popover.current && (this.popover.current as any).state.open
                ? <StyledWrapper
                    // background overlay here
                    id="overlayNew"
                    className="overlay-quickmenu"
                    style={{
                        background: "#002554",
                        opacity: .2,
                        width: "100%",
                        height: "100%",
                        top: 0,
                        left: 0,
                        position: "fixed",
                        zIndex: 9, 
                    }}
                    onClick={this.closeMenuPopup}
                    ></StyledWrapper> 
                : "" }
            </>
        );
    }
}

/** @component */
export default withTheme(QuickMenu);
